if (!customElements.get("rich-text-blocks")) {
  class RichTextBlocks extends HTMLElement {
    connectedCallback() {
      const topButton = this.querySelector(".rich-text-blocks__top-button");

      if (topButton) {
        topButton.addEventListener("click", () => this.scrollToTop());
      }

      this.#initMailchimpForms();
    }

    #initMailchimpForms() {
      const forms = this.querySelectorAll(".rich-text-blocks__mailchimp-form");
      if (!forms?.length) return;

      for (const form of forms) {
        if (form.dataset.mailchimpBound === "true") continue;
        form.dataset.mailchimpBound = "true";

        form.addEventListener("submit", (event) => {
          this.#handleMailchimpSubmit(event);
        });
      }
    }

    async #handleMailchimpSubmit(event) {
      const form = event.currentTarget;
      if (!(form instanceof HTMLFormElement)) return;

      const wrapper = form.closest(".rich-text-blocks__mailchimp");
      if (!wrapper) return;

      if (!form.action) return;

      event.preventDefault();

      const submitButton = form.querySelector('button[type="submit"]');
      const errorEl = wrapper.querySelector(".rich-text-blocks__mailchimp-error");
      const successEl = wrapper.querySelector(".rich-text-blocks__mailchimp-success");
      const genericError = wrapper.dataset.mailchimpGenericError || "";

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = "";
      }

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = true;
      }

      form.setAttribute("aria-busy", "true");

      try {
        const jsonpUrl = this.#buildMailchimpJsonpUrl(form);
        const data = await this.#jsonp(jsonpUrl, { timeoutMs: 12000 });

        if (data?.result === "success") {
          if (errorEl) {
            errorEl.hidden = true;
            errorEl.textContent = "";
          }
          form.hidden = true;
          if (successEl) {
            successEl.hidden = false;
            successEl.focus?.();
          }
          return;
        }

        const message = typeof data?.msg === "string" && data.msg.trim() ? data.msg : genericError;
        this.#showMailchimpError(errorEl, message);
      } catch (error) {
        const message = genericError || "Unable to subscribe right now.";
        this.#showMailchimpError(errorEl, message);
      } finally {
        form.removeAttribute("aria-busy");
        if (submitButton instanceof HTMLButtonElement) {
          submitButton.disabled = false;
        }
      }
    }

    #showMailchimpError(errorEl, message) {
      if (!errorEl) return;

      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    #buildMailchimpJsonpUrl(form) {
      const url = new URL(form.action, window.location.href);

      url.pathname = url.pathname.replace(/\/$/, "");
      url.pathname = url.pathname.replace(/\/post$/, "/post-json");

      const formData = new FormData(form);
      for (const [key, value] of formData.entries()) {
        if (typeof value !== "string") continue;
        if (!key) continue;
        url.searchParams.set(key, value);
      }

      return url;
    }

    #jsonp(url, { timeoutMs }) {
      return new Promise((resolve, reject) => {
        const callbackName = `__mc_jsonp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        url.searchParams.set("c", callbackName);

        const script = document.createElement("script");
        script.src = url.toString();
        script.async = true;

        const cleanup = (didTimeout = false) => {
          script.remove();
          try {
            delete window[callbackName];
          } catch {
            window[callbackName] = undefined;
          }
          if (didTimeout) {
            reject(new Error("Mailchimp JSONP timeout"));
          }
        };

        const timer = window.setTimeout(() => cleanup(true), timeoutMs);

        window[callbackName] = (data) => {
          window.clearTimeout(timer);
          script.remove();
          try {
            delete window[callbackName];
          } catch {
            window[callbackName] = undefined;
          }
          resolve(data);
        };

        script.addEventListener("error", () => {
          window.clearTimeout(timer);
          cleanup(false);
          reject(new Error("Mailchimp JSONP request failed"));
        });

        document.head.appendChild(script);
      });
    }

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }

  customElements.define("rich-text-blocks", RichTextBlocks);
}
